import { useLazyQuery } from "@apollo/client";
import { Button, Stack, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "../../common/hooks/useToast";
import { normalizePhoneInput, phoneProblem } from "../../common/phone";
import { deleteTokens, saveTokens } from "../../common/manage_tokens";
import { FETCH_USER_ROLE } from "../../graphql/queries";
import { EmailSignInError, EmailSignInStatus, useSignInWithEmail } from "./useSignInWithEmail";

export const LoginDetails: React.FC = () => {
  return <LoginWithEmail />;
};

const LoginWithEmail = () => {
  const navigate = useNavigate();
  const { error: toastError, success } = useToast();
  const [email,setEmail] = useState('');
  const [phoneNumber,setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [initialize, signInWithEmail, verifyCode, reset, signInStatus, error, token] = useSignInWithEmail();
  const [getUserRole, { data: userRole, error: userRoleError, },] = useLazyQuery(FETCH_USER_ROLE);

  const updateEmail = useCallback((event: any) => {
    setEmail(event.target.value);
    setEmailError('');
  }, []);
  const updatePhoneNumber = useCallback((event: any) => {
    setPhoneNumber(event.target.value);
    setPhoneError('');
  }, []);

  const updateOtpCode = useCallback((event: any) => {
    setOtpCode(event.target.value);
  }, []);

  const handleSubmit = useCallback((event: any) => {
    event.preventDefault();
    if (signInStatus === EmailSignInStatus.initial) {
      // Checked here because the API answers a number it cannot match with
      // silence, so a typo would otherwise wait for a code that never comes.
      const problem = phoneProblem(phoneNumber);
      setEmailError(email.trim() ? '' : 'Enter your email.');
      setPhoneError(problem);
      if (problem || !email.trim()) return;

      signInWithEmail(email.trim(), normalizePhoneInput(phoneNumber));
      return;
    }
    if (signInStatus === EmailSignInStatus.codeSent) {
      verifyCode(otpCode);
      return;
    }
  }, [signInStatus, signInWithEmail, email, phoneNumber, verifyCode, otpCode]);

  useEffect(() => {
    if (token) {
      saveTokens(token);
      getUserRole();
    }
  }, [getUserRole, token]);

  // The token is saved before the role is known only so the role query can
  // send it. Any outcome other than ADMIN must delete it again — otherwise a
  // non-admin keeps a token that RequireAuth accepts, and can open dashboard
  // routes by typing the URL.
  useEffect(() => {
    if (userRoleError) {
      deleteTokens();
      toastError("Login failed. Please try again");
      reset();
      return;
    }

    if (userRole && userRole.userRole === 'ADMIN') {
      success("Login Successful!");
        navigate("/users");
        return;
    } else if (userRole) {
      deleteTokens();
      toastError("Login failed. Please try again");
      reset();
    }
  }, [toastError, userRoleError, navigate, success, userRole, reset]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (error === EmailSignInError.emailSendingFailure) {
      toastError('Error while sending email.');
    }
    if (error === EmailSignInError.otpVerificationFailure) {
      toastError('Error while verifying code.');
    }
  }, [error, toastError]);

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {signInStatus === EmailSignInStatus.initial &&
          <>
            <TextField
              fullWidth
              label="Email"
              name="email"
              onChange={updateEmail}
              error={Boolean(emailError)}
              helperText={emailError}
              required
              value={email}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              onChange={updatePhoneNumber}
              error={Boolean(phoneError)}
              helperText={phoneError || "With the country code, for example +1 202 555 0143."}
              inputProps={{ inputMode: "tel" }}
              required
              value={phoneNumber}
            />
            <Button
            id="sign-in-with-phone"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            type="submit"
            variant="contained"
          >
            Login
          </Button>
          </>
        }
        {signInStatus === EmailSignInStatus.codeSent &&
          <>
          <TextField
            fullWidth
            label="Code"
            name="code"
            onChange={updateOtpCode}
            required
            value={otpCode}
            />
            <Button
            id="send-otp-code"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            type="submit"
            variant="contained"
          >
            Verify
          </Button>
            </>
        }

      </Stack>
    </form>
  );
};